import { message } from 'antd';
import { observable, runInAction } from 'mobx';
import { DeliveryRegistrationModel } from 'models/DeliveryRegistration';
import { PurchaseOrderDetailModel, PurchaseOrderModel } from 'models/PurchaseOrder';
import { DeliveryRegistrationReport, DeliveryResultReport, EmailSendingReport, ReceivingScheduleOfBHFReport, WasteHandoverRegistrationReport } from 'models/Report';
import { IPurchaseOrderServices, SearchDeliveryRegistration, SearchPO } from 'services/Main/purchaseOrder_services';
import { IReportServices, SearchDeliveryRegistrationReport, SearchDeliveryResultReport, SearchEmailSendingReport, SearchReceivingScheduleOfBHFReport, SearchWasteHandoverRegistrationReport, SearchWasteSummaryReport, WasteHazardousWasteQuantityOverviewReportResponse } from 'services/Main/report_services';
import { DataResponse } from 'services/response';

export type ReportStore = {
  // Báo cáo danh sách đăng ký xe
  deliveryRegistrationRes: DataResponse<DeliveryRegistrationReport[]>;
  // Báo cáo danh sách đăng ký bàn giao chất thải/phế liệu
  wasteHandoverRegistrationRes: DataResponse<WasteHandoverRegistrationReport[]>;
  // Báo cáo kết quả giao hàng
  deliveryResultRes: DataResponse<DeliveryResultReport[]>;
  // Báo cáo receiving-schedule-of-bhf
  receivingScheduleOfBHFRes: DataResponse<ReceivingScheduleOfBHFReport[]>;
  // Báo cáo kết quả gửi email
  emailSendingRes: DataResponse<EmailSendingReport[]>;
  // Báo cáo tổng hợp chất thải/phế liệu
  wasteSummaryRes: DataResponse<any[]>;
  // Tổng khối lượng cho báo cáo tổng hợp chất thải/phế liệu
  wasteSummaryTotalQuantity?: number;

  // tổng hợp chất thải nguy hại
  hazardousWasteQuantityOverviewRes: DataResponse<WasteHazardousWasteQuantityOverviewReportResponse | undefined>;
  // tổng hợp chất thải công nghiệp thông thường & chất thải rắn sinh hoạt
  noneHazardousWasteQuantityOverviewRes: DataResponse<WasteHazardousWasteQuantityOverviewReportResponse | undefined>;
};

export interface IReportPresenter {
  createStore(): ReportStore;
  // ======================= BÁO CÁO DANH SÁCH ĐĂNG KÝ XE ==========================
  getListReportDeliveryRegistration(store: ReportStore, data: SearchDeliveryRegistrationReport): Promise<void>;
  // Export excel
  exportListReportDeliveryRegistration(data: SearchDeliveryRegistrationReport): Promise<void>;
  //====================== BÁO CÁO DANH SÁCH ĐĂNG KÝ BÀN GIAO CHẤT THẢI/PHẾ LIỆU ==========================
  //  Search
  getWasteHandoverRegistrationReport(store: ReportStore, data: SearchWasteHandoverRegistrationReport): Promise<void>;
  // Export excel
  exportWasteHandoverRegistrationReport(data: Omit<SearchWasteHandoverRegistrationReport, 'paging'>): Promise<void>;
  //====================== BÁO CÁO KẾT QUẢ GIAO HÀNG =================================================
  //  Search
  getReportDeliveryResult(store: ReportStore, data: SearchDeliveryResultReport): Promise<void>
  // Export excel
  exportReportDeliveryResult(data: Omit<SearchDeliveryResultReport, 'paging'>): Promise<void>;
  //====================== BÁO CÁO RECEIVING-SCHEDULE-OF-BHF =================================================
  //  Search
  getReceivingSchedulReport(store: ReportStore, data: SearchReceivingScheduleOfBHFReport): Promise<void>
  // Export excel
  exportReceivingSchedulReport(data: Omit<SearchReceivingScheduleOfBHFReport, 'paging'>): Promise<void>;
  //====================== BÁO CÁO KẾT QUẢ GỬI EMAIL =================================================
  //  Search
  getEmailSendingReport(store: ReportStore, data: SearchEmailSendingReport): Promise<void>
  // Export excel
  exportEmailSendingReport(data: Omit<SearchEmailSendingReport, 'paging'>): Promise<void>;
  //====================== BÁO CÁO TỔNG HỢP CHẤT THẢI/PHẾ LIỆU ===============================================
  //  Search
  getWasteSummaryReport(store: ReportStore, data: SearchWasteSummaryReport): Promise<void>;
  // Export excel
  exportWasteSummaryReport(data: SearchWasteSummaryReport): Promise<void>;

  // báo cáo chất tổng hợp chất thải nguy hại
  reportHazardousWasteQuantityOverview(store: ReportStore, data: object): void;
  exportHazardousWasteQuantityOverview(data: object): void;

  // báo cáo tổng hợp khối lượng chất thải công nghiệp thông thường & chất thải rắn sinh hoạt
  reportNoneHazardousWasteQuantityOverview(store: ReportStore, data: object): void;
  exportNoneHazardousWasteQuantityOverview(data: object): void;
}

export function createReportPresenter({ reportServices }: { reportServices: IReportServices }): IReportPresenter {
  return {
    createStore: (): ReportStore =>
      observable({
        // Báo cáo danh sách đăng ký xe
        deliveryRegistrationRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        // Báo cáo danh sách đăng ký bàn giao chất thải/phế liệu
        wasteHandoverRegistrationRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        // Báo cáo kết quả giao hàng
        deliveryResultRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        // Báo cáo receiving-schedule-of-bhf
        receivingScheduleOfBHFRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        // Báo cáo kết quả gửi email
        emailSendingRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        // Báo cáo tổng hợp chất thải/phế liệu
        wasteSummaryRes: {
          data: [],
          isSuccess: false,
          message: ''
        },
        wasteSummaryTotalQuantity: undefined,

        hazardousWasteQuantityOverviewRes: {
          data: undefined,
          isSuccess: false,
          message: ''
        },
        noneHazardousWasteQuantityOverviewRes: {
          data: undefined,
          isSuccess: false,
          message: ''
        },
      }),
    // ======================= BÁO CÁO DANH SÁCH ĐĂNG KÝ XE ==========================
    getListReportDeliveryRegistration: async (store, data) => {
      try {
        const result = await reportServices.getListReportDeliveryRegistration(data);
        runInAction(() => {
          store.deliveryRegistrationRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    // Export excel
    exportListReportDeliveryRegistration: async (data) => {
      try {
        await reportServices.exportListReportDeliveryRegistration(data);
      } catch (error) {
        console.log(error);
      }
    },
    //====================== BÁO CÁO DANH SÁCH ĐĂNG KÝ BÀN GIAO CHẤT THẢI/PHẾ LIỆU ==========================
    //  Search
    getWasteHandoverRegistrationReport: async (store, data) => {
      try {
        const result = await reportServices.getWasteHandoverRegistrationReport(data);
        runInAction(() => {
          store.wasteHandoverRegistrationRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    // Export excel
    exportWasteHandoverRegistrationReport: async (data) => {
      try {
        await reportServices.exportWasteHandoverRegistrationReport(data);
      } catch (error) {
        console.log(error);
      }
    },
    //====================== BÁO CÁO KẾT QUẢ GIAO HÀNG =================================================
    //  Search
    getReportDeliveryResult: async (store, data) => {
      try {
        const result = await reportServices.getReportDeliveryResult(data);
        runInAction(() => {
          store.deliveryResultRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    // Export excel
    exportReportDeliveryResult: async (data) => {
      try {
        await reportServices.exportReportDeliveryResult(data);
      } catch (error) {
        console.log(error);
      }
    },
    //====================== BÁO CÁO RECEIVING-SCHEDULE-OF-BHF =================================================
    //  Search
    getReceivingSchedulReport: async (store, data) => {
      try {
        const result = await reportServices.getReceivingSchedulReport(data);
        runInAction(() => {
          store.receivingScheduleOfBHFRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    // Export excel
    exportReceivingSchedulReport: async (data) => {
      try {
        await reportServices.exportReceivingSchedulReport(data);
      } catch (error) {
        console.log(error);
      }
    },
    //====================== BÁO CÁO KẾT QUẢ GỬI EMAIL =================================================
    //  Search
    getEmailSendingReport: async (store, data) => {
      try {
        const result = await reportServices.getEmailSendingReport(data);
        runInAction(() => {
          store.emailSendingRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    exportEmailSendingReport: async (data) => {
      try {
        await reportServices.exportEmailSendingReport(data);
      } catch (error) {
        console.log(error);
      }
    },
    //====================== BÁO CÁO TỔNG HỢP CHẤT THẢI/PHẾ LIỆU ===============================================
    //  Search
    getWasteSummaryReport: async (store, data) => {
      try {
        const result = await reportServices.getWasteSummaryReport(data);
        runInAction(() => {
          // Tách totalQuantity ra khỏi result trước khi gán vào wasteSummaryRes
          const { totalQuantity, ...dataResponse } = result as any;
          store.wasteSummaryRes = dataResponse;
          store.wasteSummaryTotalQuantity = totalQuantity;
        });
      } catch (error) {
        console.log(error);
      }
    },
    exportWasteSummaryReport: async (data) => {
      try {
        await reportServices.exportWasteSummaryReport(data);
      } catch (error) {
        console.log(error);
      }
    },


    // báo cáo chất tổng hợp chất thải nguy hại
    reportHazardousWasteQuantityOverview: async (store, data) => {
      try {
        const result = await reportServices.reportHazardousWasteQuantityOverview(data);
        runInAction(() => {
          store.hazardousWasteQuantityOverviewRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    exportHazardousWasteQuantityOverview: async (data) => {
      try {
        await reportServices.exportHazardousWasteQuantityOverview(data);
      } catch (error) {
        console.log(error);
      }
    },

    // báo cáo tổng hợp khối lượng chất thải công nghiệp thông thường & chất thải rắn sinh hoạt
    reportNoneHazardousWasteQuantityOverview: async (store, data) => {
      try {
        const result = await reportServices.reportNoneHazardousWasteQuantityOverview(data);
        runInAction(() => {
          store.noneHazardousWasteQuantityOverviewRes = result;
        });
      } catch (error) {
        console.log(error);
      }
    },
    exportNoneHazardousWasteQuantityOverview: async (data) => {
      try {
        await reportServices.exportNoneHazardousWasteQuantityOverview(data);
      } catch (error) {
        console.log(error);
      }
    },
  };
}
